import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ShieldCheck, Globe, MapPin, Lock, ArrowRight, Quotes } from "@phosphor-icons/react";

export default function Network() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/members/public").then(({ data }) => setData(data));
  }, []);

  const total = data?.total_members ?? 0;
  const countryCount = data?.total_countries ?? 0;
  const countries = data?.countries || [];

  return (
    <section className="max-w-7xl mx-auto px-6 py-16 sm:py-24" data-testid="network-page">
      {/* Header */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-14">
        <div className="md:col-span-7">
          <div className="overline mb-4">The network</div>
          <h1 className="font-display text-5xl sm:text-6xl font-black text-navy tracking-tighter">
            Curated. Vetted.<br />
            <span className="text-ember">Territory-protected.</span>
          </h1>
        </div>
        <div className="md:col-span-5">
          <p className="text-navy/70">
            EPAS does not publish its member list. Member identity, contact details and trade-lane
            positions are strictly reserved for approved partners — that is the core promise of a
            curated network. Below is a transparent, privacy-preserving view of our footprint.
          </p>
        </div>
      </div>

      {/* Big numbers */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10 border border-black/10 mb-16">
        <Big label="Members" value={total} />
        <Big label="Countries" value={countryCount} />
        <Big label="Strategic corridors" value={6} />
        <Big label="Operating since" value={2014} />
      </div>

      {/* Exclusivity narrative */}
      <div className="bg-navy text-white p-10 md:p-14 grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
        <div className="md:col-span-5 flex items-start gap-4">
          <ShieldCheck size={44} weight="duotone" className="text-ember shrink-0" />
          <div>
            <div className="overline !text-ember">Territory policy</div>
            <h3 className="font-display text-3xl font-bold mt-3">One partner per territory.</h3>
          </div>
        </div>
        <div className="md:col-span-7 text-white/80 leading-relaxed">
          <p>
            EPAS limits the number of active members per country to protect each partner's income and
            reputation. In large freight economies (USA, China, Germany) the cap is higher; in most
            markets it is strict. Members hold an exclusive seat for their country or sub-region for
            as long as they remain in good standing.
          </p>
          <p className="mt-4">
            We don't publish member names — that protects the commercial interests of the people
            already inside. If you are approved, you join a group where cooperation is real because
            competition between members is controlled by design.
          </p>
        </div>
      </div>

      {/* Country coverage */}
      <div className="border border-black/10 bg-white p-8 md:p-10 mb-16" data-testid="network-countries">
        <div className="flex items-center gap-3 mb-6">
          <Globe size={20} weight="duotone" className="text-ember" />
          <div className="overline">Coverage — countries represented</div>
        </div>
        {countries.length === 0 ? (
          <p className="text-navy/60 text-sm">Loading…</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {countries.map((c) => (
              <span key={c} className="inline-flex items-center gap-1.5 border border-navy/15 text-navy/80 text-sm px-3 py-1.5" data-testid={`country-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}>
                <MapPin size={12} /> {c}
              </span>
            ))}
          </div>
        )}
        <p className="mt-6 text-xs text-navy/50 border-t border-black/5 pt-4">
          Company names and contact details are available only to approved members via the member portal.
        </p>
      </div>

      {/* Testimonials (placeholder — intentionally generic, no invented companies) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
        {[
          { q: "A small network where introductions are active, not passive. Exactly what we were looking for.", who: "Founding member · Central Europe" },
          { q: "Territory protection is not marketing copy here — it is how EPAS actually operates day to day.", who: "Member · Adriatic corridor" },
          { q: "The vetting process is serious. That is why we joined.", who: "Member · Middle East" },
        ].map((t, i) => (
          <div key={i} className="bg-white border border-black/10 p-6">
            <Quotes size={22} weight="duotone" className="text-ember mb-3" />
            <p className="text-navy/80 text-sm leading-relaxed">{t.q}</p>
            <div className="mt-4 text-[10px] uppercase tracking-widest text-navy/50">{t.who}</div>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="border border-black/10 bg-white p-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
        <div className="md:col-span-8">
          <div className="overline mb-3">Interested in a seat?</div>
          <h3 className="font-display text-3xl font-bold text-navy">Your territory may still be open.</h3>
          <p className="text-navy/70 mt-2">Most applications are reviewed within 5–10 business days.</p>
        </div>
        <div className="md:col-span-4 flex md:justify-end gap-3">
          <Link to="/apply" className="btn-primary" data-testid="network-apply-cta">Apply <ArrowRight size={14} /></Link>
          <Link to="/member-login" className="btn-secondary" data-testid="network-member-login">
            <Lock size={14} /> Members
          </Link>
        </div>
      </div>
    </section>
  );
}

const Big = ({ label, value }) => (
  <div className="bg-white p-8">
    <div className="ship-number text-5xl md:text-6xl font-black text-navy leading-none">{value}</div>
    <div className="mt-3 overline">{label}</div>
  </div>
);
